import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { creatorProfile, creatorVideoIds } from "@/mocks/videos";
import { getVideosByIds } from "@/lib/videoData";
import { formatCount } from "@/lib/format";

export default function CreatorPage() {
  const [tip, setTip] = useState("");
  const myVideos = getVideosByIds(creatorVideoIds);

  usePageMeta({
    title: "创作者中心 - 快手短视频平台",
    description:
      "快手创作者中心，提供作品数据概览、发布管理与创作指南，助力内容创作者持续成长，让优质作品被更多人看到。",
    keywords: "快手创作者,创作中心,短视频创作,内容创作者,作品数据",
  });

  const showTip = (msg: string) => {
    setTip(msg);
    window.setTimeout(() => setTip(""), 2600);
  };

  const stats = [
    { label: "播放量总计", value: formatCount(creatorProfile.totalViews), icon: "ri-play-circle-line" },
    { label: "粉丝数", value: formatCount(creatorProfile.followers), icon: "ri-user-heart-line" },
    { label: "获赞总数", value: formatCount(creatorProfile.totalLikes), icon: "ri-thumb-up-line" },
    { label: "作品数量", value: `${creatorProfile.videoCount}`, icon: "ri-vidicon-line" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8 space-y-8">
          <section className="rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-6 py-8 md:px-10 md:py-10">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden bg-background-200 ring-4 ring-background-50/30 shrink-0">
                <img
                  src={creatorProfile.avatar}
                  alt={creatorProfile.name}
                  className="w-full h-full object-cover object-top"
                />
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-bold text-background-50">
                    {creatorProfile.name}
                  </h1>
                  <span className="px-2 py-0.5 rounded-full bg-background-50/20 text-background-50 text-xs">
                    认证创作者
                  </span>
                </div>
                <p className="text-sm text-background-50/85 mt-2 max-w-2xl">{creatorProfile.bio}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => showTip("功能演示，暂不支持真实上传")}
                  className="flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-background-50 text-primary-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-2-line"></i>
                  上传视频
                </button>
                <Link
                  to="/about"
                  className="flex items-center justify-center gap-2 h-11 px-6 rounded-full border border-background-50/60 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-2-line"></i>
                  创作指南
                </Link>
              </div>
            </div>

            {tip && (
              <p className="text-xs text-background-50 mt-4 flex items-center gap-1 animate-fade-in">
                <i className="ri-information-line"></i>
                {tip}
              </p>
            )}
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-line-chart-line text-accent-500"></i>
              作品数据概览
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="w-10 h-10 rounded-full bg-accent-50 text-accent-600 flex items-center justify-center text-xl mb-3">
                    <i className={s.icon}></i>
                  </span>
                  <p className="text-2xl font-bold text-foreground-950">{s.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-400 mt-3">
              * 以上为假设演示数据，仅用于展示创作者中心界面效果。
            </p>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-folder-video-line text-primary-500"></i>
                我发布的视频
              </h2>
              <span className="text-sm text-foreground-400">共 {myVideos.length} 条示例作品</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
              {myVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950 mb-4">创作者扶持计划</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  icon: "ri-money-cny-circle-line",
                  title: "流量与收益扶持",
                  desc: "优质内容可获得平台推荐流量与创作激励，让好作品被更多人看见。",
                },
                {
                  icon: "ri-graduation-cap-line",
                  title: "创作课程学习",
                  desc: "提供拍摄、剪辑、选题等系列课程，帮助你从入门到进阶。",
                },
                {
                  icon: "ri-team-line",
                  title: "社区交流成长",
                  desc: "加入创作者社区，与同好交流经验，一起解决创作中的难题。",
                },
              ].map((item) => (
                <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-2xl mb-3">
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="text-base font-medium text-foreground-900">{item.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}